import React, {useState, useEffect} from "react";
import {Link} from "react-router-dom";
import {Message, User, EmojiType} from "../types/Types";
import {addAnchorTags, getDate, getMessageDate, getTime} from "../util/Util";
import UserTag from "../users/tag/UserTag";
import {faTrash} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import EmojiReactionPicker, { EMOJI_TYPE_TO_DISPLAY } from "./EmojiReactionPicker";

interface Props {
    message: Message;
    user: User | undefined;
    currentUser: User;
    hideMessage: (arg0: string) => void;
    previousMessage: Message | undefined;
    addReaction: (messageId: string, emojiType: EmojiType) => void;
    openEmojiPickerMessageId: string | null;
    setOpenEmojiPickerMessageId: (messageId: string | null) => void;
}

function MessageRow(props: Props) {
    const [pickerPosition, setPickerPosition] = useState({ x: 0, y: 0 });
    const showEmojiPicker = props.openEmojiPickerMessageId === props.message.id;

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            // Check if click is on emoji picker or message bubble
            const target = e.target as HTMLElement;
            const isEmojiPicker = target.closest('.emoji-reaction-picker');
            const isChatBubble = target.closest('.chat-bubble');
            
            if (showEmojiPicker && !isEmojiPicker && !isChatBubble) {
                props.setOpenEmojiPickerMessageId(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showEmojiPicker]);

    function isOwnMessage(): boolean {
        return props.currentUser.id == props.message.userId
    }

    function shouldDisplayName(): boolean {
        if(props.previousMessage == undefined) {
            return true;
        }
        return (props.message.userId != props.previousMessage.userId)
            || (props.message.createdAt as any).seconds - (props.previousMessage.createdAt as any).seconds > 300
    }

    const handleMessageClick = (e: React.MouseEvent | React.TouchEvent) => {
        e.stopPropagation();
        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        
        // Center the picker horizontally on the click point
        const centeredX = clientX - 117;
        
        // Toggle picker - if already open, close it; otherwise open at new position
        if (showEmojiPicker) {
            props.setOpenEmojiPickerMessageId(null);
        } else {
            setPickerPosition({ x: centeredX, y: clientY - 50 });
            props.setOpenEmojiPickerMessageId(props.message.id);
        }
    };

    const handleEmojiSelect = (emojiType: EmojiType) => {
        props.addReaction(props.message.id, emojiType);
        props.setOpenEmojiPickerMessageId(null);
    };

    const handleReactionClick = (emojiType: EmojiType, e: React.MouseEvent) => {
        e.stopPropagation();
        props.addReaction(props.message.id, emojiType);
    };

    const getReactionElements = () => {
        if (!props.message.reactions) return null;
        
        // Calculate counts for each emoji type from the userId->emojiType map
        const emojiCounts: Record<EmojiType, { count: number; userIds: string[] }> = {
            [EmojiType.HEART]: { count: 0, userIds: [] },
            [EmojiType.LAUGH]: { count: 0, userIds: [] },
            [EmojiType.SHOCKED]: { count: 0, userIds: [] },
            [EmojiType.CRY]: { count: 0, userIds: [] },
            [EmojiType.ANGER]: { count: 0, userIds: [] },
            [EmojiType.THUMB_UP]: { count: 0, userIds: [] },
            [EmojiType.THUMB_DOWN]: { count: 0, userIds: [] }
        };

        // Iterate through reactions map and count each emoji type
        Object.entries(props.message.reactions).forEach(([userId, emojiType]) => {
            if (emojiCounts[emojiType]) {
                emojiCounts[emojiType].count += 1;
                emojiCounts[emojiType].userIds.push(userId);
            }
        });

        // Create reaction badges for emojis that have reactions
        const reactions = Object.entries(emojiCounts)
            .filter(([emojiType, data]) => data.count > 0)
            .map(([emojiType, data]) => {
                const userReacted = data.userIds.includes(props.currentUser.id);
                return (
                    <span
                        key={emojiType}
                        className={`reaction-badge ${userReacted ? 'user-reacted' : ''}`}
                        onClick={(e) => handleReactionClick(emojiType as EmojiType, e)}
                        title={`${data.count} reaction${data.count > 1 ? 's' : ''}`}
                    >
                        {EMOJI_TYPE_TO_DISPLAY[emojiType as EmojiType]} {data.count}
                    </span>
                );
            });
        
        return reactions.length > 0 ? (
            <div className="message-reactions" style={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                marginLeft: '0',
                marginTop: '4px',
                marginBottom: '2px',
                marginRight: '-4px'
            }}>
                {reactions}
            </div>
        ) : null;
    };
    
    return (
        <>
            {showEmojiPicker && (
                <EmojiReactionPicker
                    onEmojiSelect={handleEmojiSelect}
                    onClose={() => props.setOpenEmojiPickerMessageId(null)}
                    position={pickerPosition}
                />
            )}
            {<div className={"chat-message mt-0 ml-1"}>
                {!isOwnMessage() && shouldDisplayName() && <div className={"ml-2 mt-2"} style={{marginBottom: "0px"}}>
                    <>
                        <div className={"d-inline-block"}>
                            <small><UserTag user={props.user} visit={undefined} session={undefined} padRight={false} hideLocationDot={false} linksToTab={"visits"} /></small>
                        </div>
                        <small style={{marginLeft: "5px", color: "grey"}}>{getMessageDate(props.message.createdAt)}</small>
                    </>
                </div>}
                <div 
                    className={`chat-bubble ${isOwnMessage() ? "chat-bubble-right" : ""}`} 
                    style={{marginTop: "4px", marginBottom: "4px", cursor: "pointer"}}
                    onClick={handleMessageClick}

                >
                    <span className={""}>
                        <span dangerouslySetInnerHTML={{ __html: addAnchorTags(props.message.payload)}} />
                    </span>
                    {props.currentUser.chatAdmin && <div className={"d-inline-block"}>
                        <small className={"ml-1"} style={isOwnMessage() ? {color: "#FFFFFF"} : {color: "#808080"}}>
                            {(props.user && (props.user.id == props.message.userId || props.user.admin)) &&
                            <FontAwesomeIcon style={{cursor: "pointer"}}
                                             onClick={() => {props.hideMessage(props.message.id)}}
                                             icon={faTrash} />}
                        </small>
                    </div>}
                    {getReactionElements()}
                </div>
            </div>}
        </>
    )
}

export default MessageRow;